import { useState, useEffect, useCallback, useMemo } from 'react';
import Logo from '../Image/Client/Logo.svg';
import AppStore from '../Image/Client/appStore.svg';
import PlayStore from '../Image/Client/playstore.svg';
import { FaApple, FaGooglePlay, FaShieldAlt, FaBolt, FaPhone, FaServer, FaMobileAlt, FaStar, FaChevronUp } from 'react-icons/fa';
import { HiMenu, HiX, HiMail, HiDownload, HiLockClosed, HiSparkles } from 'react-icons/hi';

const Client = () => {
    const [activeTab, setActiveTab] = useState('privacy');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // App store links
    const appStoreLink = "https://apps.apple.com/us/app/true-caller-name/id6756807903";
    const playStoreLink = "https://play.google.com/store/apps/details?id=com.callsense.identifier.trustcaller.id";

    // Navigation items
    const navItems = useMemo(() => [
        { name: 'Features', id: 'features' },
        { name: 'How It Works', id: 'how-it-works' },
        { name: 'Download', id: 'download' },
        { name: 'Legal', id: 'legal' },
        { name: 'Contact', id: 'contact' }
    ], []);

    // Handle scroll events
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
            setShowScrollTop(window.scrollY > 500);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Simulate loading
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    // Close mobile menu on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') setMobileMenuOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Close mobile menu on resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) setMobileMenuOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Scroll to section
    const scrollToSection = useCallback((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
        setMobileMenuOpen(false);
    }, []);

    // Scroll to top
    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Features data
    const features = useMemo(() => [
        {
            icon: <FaShieldAlt className="w-7 h-7" />,
            title: '100% Privacy Safe',
            description: 'All data processing happens locally on your device. No personal information is ever sent to external servers.',
            gradient: 'from-emerald-500 to-green-600',
            bgGlow: 'group-hover:shadow-emerald-500/25'
        },
        {
            icon: <FaBolt className="w-7 h-7" />,
            title: 'Lightning Fast',
            description: "Instant caller identification with minimal battery usage. Lightweight app that won't slow down your phone.",
            gradient: 'from-amber-500 to-orange-500',
            bgGlow: 'group-hover:shadow-amber-500/25'
        },
        {
            icon: <FaPhone className="w-7 h-7" />,
            title: 'Real-Time Caller ID',
            description: "See who's calling before you answer. Works with both incoming and outgoing calls seamlessly.",
            gradient: 'from-blue-500 to-indigo-600',
            bgGlow: 'group-hover:shadow-blue-500/25'
        },
        {
            icon: <FaServer className="w-7 h-7" />,
            title: 'No Backend Servers',
            description: 'We operate no data collection infrastructure. Your contacts and call logs stay on your device only.',
            gradient: 'from-violet-500 to-purple-600',
            bgGlow: 'group-hover:shadow-violet-500/25'
        },
        {
            icon: <FaMobileAlt className="w-7 h-7" />,
            title: 'Simple Interface',
            description: 'Clean, intuitive design that just works. No complicated settings or confusing options.',
            gradient: 'from-pink-500 to-rose-600',
            bgGlow: 'group-hover:shadow-pink-500/25'
        },
        {
            icon: <FaStar className="w-7 h-7" />,
            title: 'Free to Use',
            description: 'Access all essential features completely free. Supported by non-intrusive advertisements.',
            gradient: 'from-cyan-500 to-teal-600',
            bgGlow: 'group-hover:shadow-cyan-500/25'
        },
    ], []);

    // How it works steps
    const steps = useMemo(() => [
        { step: '01', title: 'Download', desc: 'Get the app from Play Store or App Store', icon: '📲' },
        { step: '02', title: 'Grant Permissions', desc: 'Allow access to calls & contacts (stays local)', icon: '✅' },
        { step: '03', title: 'Stay Protected', desc: "Know who's calling before you answer", icon: '🛡️' }
    ], []);

    // Stats data
    const stats = useMemo(() => [
        { value: '100%', label: 'Privacy Safe', icon: <HiLockClosed className="w-5 h-5" /> },
        { value: '0 KB', label: 'Data Uploaded', icon: <HiSparkles className="w-5 h-5" /> },
        { value: '<1MB', label: 'App Size', icon: <FaBolt className="w-5 h-5" /> }
    ], []);

    // Privacy policy items
    const privacyItems = useMemo(() => [
        { num: 1, title: 'No Personal Data Collection', content: 'We do not collect, store, or transmit any personal information. Caller Name accesses your call logs and contacts only on your device to display real-time caller identification. This data never leaves your phone.' },
        { num: 2, title: 'Device Permissions', content: 'Permissions such as READ_PHONE_STATE, READ_CALL_LOG, PROCESS_OUTGOING_CALLS, and READ_CONTACTS are used exclusively for local Caller ID functionality. They are not used for tracking or profiling.' },
        { num: 3, title: 'Advertising (Google AdMob)', content: 'The App uses Google AdMob to display ads. AdMob may collect non-personally identifiable information such as your Advertising ID to serve relevant ads. You can opt out via Android Settings > Google > Ads.' },
        { num: 4, title: 'Data Sharing', content: 'We do not sell, rent, or share your personal information with third parties. The only data shared is what is automatically processed by Google AdMob for ad delivery.' },
        { num: 5, title: "Children's Privacy", content: 'Caller Name is not intended for users under the age of 13. We do not knowingly collect data from children.' },
        { num: 6, title: 'Policy Updates', content: 'We may update this Privacy Policy from time to time. The latest version will always be accessible within the App or upon request via email.' },
        { num: 7, title: 'Contact Us', content: 'If you have any questions, please contact us at: contact@vedicantechnology.com' },
    ], []);

    // Terms items
    const termsItems = useMemo(() => [
        { num: 1, title: 'Service Description', content: 'Caller Name is a lightweight application that displays caller identification information for incoming and outgoing calls using data stored locally on your device. The App does not record or share your communications.' },
        { num: 2, title: 'Permissions', content: 'The App requires specific permissions—including READ_PHONE_STATE, READ_CALL_LOG, PROCESS_OUTGOING_CALLS, and READ_CONTACTS—solely to display caller information. All data processing occurs locally.' },
        { num: 3, title: 'Advertisements', content: 'This App displays ads exclusively through Google AdMob. We do not control the content or targeting of these ads. You may disable personalized ads via your device settings.' },
        { num: 4, title: 'No Warranty', content: 'The App is provided "as is" and "as available." We do not guarantee uninterrupted or error-free performance.' },
        { num: 5, title: 'Limitation of Liability', content: 'Vedicant Technology shall not be liable for any indirect, incidental, or consequential damages arising from your use of Caller Name.' },
        { num: 6, title: 'Changes to Terms', content: 'We reserve the right to update these Terms at any time. Continued use of the App constitutes acceptance of the revised terms.' },
        { num: 7, title: 'Governing Law', content: 'These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in India.' },
        { num: 8, title: 'Contact', content: 'For questions about these Terms, please email us at: contact@vedicantechnology.com' },
    ], []);

    // Recent calls for phone mockup
    const recentCalls = useMemo(() => [
        { name: 'Sarah Johnson', number: '+1 (555) 987-6543', time: '2m', color: 'from-purple-500 to-purple-600' },
        { name: 'Mike Wilson', number: '+1 (555) 456-7890', time: '15m', color: 'from-orange-500 to-orange-600' },
        { name: 'Emma Davis', number: '+1 (555) 321-0987', time: '1h', color: 'from-pink-500 to-pink-600' }
    ], []);

    // Loading screen
    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-main-white flex items-center justify-center z-50">
                <div className="text-center">
                    <img src={Logo} alt="Caller Name" className="h-16 w-16 mx-auto mb-4 animate-pulse" />
                    <div className="w-32 h-1 bg-main-th rounded-full overflow-hidden mx-auto">
                        <div className="h-full bg-main-blue rounded-full animate-[loading_1s_ease-in-out_infinite]"
                            style={{ width: '60%', animation: 'loading 1s ease-in-out infinite' }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full text-main-font min-h-screen bg-main-bg">
            {/* Skip to content link for accessibility */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-main-blue focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
            >
                Skip to main content
            </a>

            {/* Navigation */}
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                    ? 'bg-main-white/95 backdrop-blur-xl shadow-lg shadow-black/5'
                    : 'bg-transparent'
                    }`}
                role="navigation"
                aria-label="Main navigation"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 sm:h-20">
                        {/* Logo */}
                        <button
                            onClick={scrollToTop}
                            className="flex items-center gap-2 sm:gap-3 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2 rounded-lg p-1"
                            aria-label="Go to top"
                        >
                            <img
                                src={Logo}
                                alt=""
                                className="h-9 w-9 sm:h-11 sm:w-11 drop-shadow-md"
                                loading="eager"
                            />
                            <span className="text-base sm:text-lg font-bold text-main-font">Caller Name</span>
                        </button>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
                            {navItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    className="relative text-main-subfont hover:text-main-blue transition-colors font-medium group py-2 focus:outline-none focus:text-main-blue"
                                    aria-label={`Navigate to ${item.name}`}
                                >
                                    {item.name}
                                    <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-main-blue transition-all duration-300 group-hover:w-full group-focus:w-full rounded-full" />
                                </button>
                            ))}
                        </div>

                        {/* Desktop CTA */}
                        <div className="hidden lg:block">
                            <button
                                onClick={() => scrollToSection('download')}
                                className="bg-main-blue text-main-white px-5 xl:px-6 py-2.5 rounded-full font-semibold hover:bg-main-blue/90 transition-all hover:shadow-xl hover:shadow-main-blue/25 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2 flex items-center gap-2"
                            >
                                <HiDownload className="w-4 h-4" />
                                <span>Get the App</span>
                            </button>
                        </div>

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2.5 text-main-font rounded-xl hover:bg-main-th transition-colors focus:outline-none focus:ring-2 focus:ring-main-blue"
                            aria-expanded={mobileMenuOpen}
                            aria-controls="mobile-menu"
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            {mobileMenuOpen
                                ? <HiX className="w-6 h-6" />
                                : <HiMenu className="w-6 h-6" />
                            }
                        </button>
                    </div>

                    {/* Mobile Menu */}
                    <div
                        id="mobile-menu"
                        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-[500px] opacity-100 pb-4' : 'max-h-0 opacity-0'
                            }`}
                        aria-hidden={!mobileMenuOpen}
                    >
                        <div className="bg-main-white rounded-2xl p-3 shadow-xl border border-main-border/50">
                            {navItems.map((item, index) => (
                                <button
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    className="block w-full text-left text-main-subfont hover:text-main-blue hover:bg-main-th/50 transition-all font-medium py-3.5 px-4 rounded-xl focus:outline-none focus:bg-main-th"
                                    tabIndex={mobileMenuOpen ? 0 : -1}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    {item.name}
                                </button>
                            ))}
                            <button
                                onClick={() => scrollToSection('download')}
                                className="bg-main-blue text-main-white px-6 py-3.5 rounded-xl font-semibold w-full mt-2 flex items-center justify-center gap-2 hover:bg-main-blue/90 transition-colors focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2"
                                tabIndex={mobileMenuOpen ? 0 : -1}
                            >
                                <HiDownload className="w-5 h-5" />
                                Get the App
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Overlay for mobile menu */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-hidden="true"
                />
            )}

            <main id="main-content">
                {/* Hero Section */}
                <section className="relative pt-20 sm:pt-24 lg:pt-28 pb-12 sm:pb-14 lg:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                    {/* Background decorations */}
                    <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] lg:w-[600px] h-[300px] sm:h-[500px] lg:h-[600px] bg-gradient-to-br from-main-blue/10 via-main-th to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-[200px] sm:w-[300px] lg:w-[400px] h-[200px] sm:h-[300px] lg:h-[400px] bg-gradient-to-tr from-main-blue/5 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />
                    <div className="absolute top-40 left-10 w-16 lg:w-20 h-16 lg:h-20 bg-main-blue/10 rounded-2xl rotate-12 animate-pulse hidden lg:block pointer-events-none" />
                    <div className="absolute bottom-40 right-20 w-12 lg:w-16 h-12 lg:h-16 bg-main-th rounded-full animate-bounce hidden lg:block pointer-events-none" style={{ animationDuration: '3s' }} />

                    <div className="max-w-7xl mx-auto relative">
                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 xl:gap-12 items-center">
                            {/* Hero Content */}
                            <div className="text-center lg:text-left order-2 lg:order-1">
                                {/* Badge */}
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-main-th to-main-blue/10 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full mb-5 sm:mb-6 border border-main-blue/20">
                                    <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-main-blue opacity-75" />
                                        <span className="relative inline-flex rounded-full h-full w-full bg-main-blue" />
                                    </span>
                                    <span className="text-xs sm:text-sm font-semibold text-main-blue">100% Privacy Focused</span>
                                </div>

                                {/* Heading */}
                                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-main-font leading-[1.1] mb-5 sm:mb-6">
                                    Know Who's
                                    <span className="relative block mt-1 sm:mt-2">
                                        <span className="text-main-blue">Calling You</span>
                                        <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3 text-main-blue/30" viewBox="0 0 200 12" fill="none" aria-hidden="true">
                                            <path d="M2 10C50 2 150 2 198 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                </h1>

                                {/* Description */}
                                <p className="text-base sm:text-lg lg:text-xl text-main-subfont mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                                    Identify unknown callers instantly with our lightweight, privacy-first caller ID app.
                                    <span className="text-main-font font-medium"> No data leaves your device—ever.</span>
                                </p>

                                {/* Download Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                                    <a
                                        href={playStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center justify-center sm:justify-start gap-3 sm:gap-4 bg-main-font text-main-white px-5 sm:px-7 py-3.5 sm:py-4 rounded-2xl hover:bg-main-font/90 transition-all hover:shadow-2xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-main-font focus:ring-offset-2"
                                        aria-label="Download from Google Play Store"
                                    >
                                        <FaGooglePlay className="h-7 w-7 sm:h-8 sm:w-8 group-hover:scale-110 transition-transform flex-shrink-0" />
                                        <div className="text-left">
                                            <div className="text-[10px] sm:text-xs opacity-70 uppercase tracking-wider">Get it on</div>
                                            <div className="text-base sm:text-lg font-bold">Google Play</div>
                                        </div>
                                    </a>
                                    <a
                                        href={appStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center justify-center sm:justify-start gap-3 sm:gap-4 bg-main-font text-main-white px-5 sm:px-7 py-3.5 sm:py-4 rounded-2xl hover:bg-main-font/90 transition-all hover:shadow-2xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-main-font focus:ring-offset-2"
                                        aria-label="Download from Apple App Store"
                                    >
                                        <FaApple className="h-8 w-8 sm:h-9 sm:w-9 group-hover:scale-110 transition-transform flex-shrink-0" />
                                        <div className="text-left">
                                            <div className="text-[10px] sm:text-xs opacity-70 uppercase tracking-wider">Download on</div>
                                            <div className="text-base sm:text-lg font-bold">App Store</div>
                                        </div>
                                    </a>
                                </div>

                                {/* Stats */}
                                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 lg:gap-8 mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-main-border/50">
                                    {stats.map((stat, index) => (
                                        <div key={index} className="text-center lg:text-left">
                                            <div className="flex items-center gap-1.5 sm:gap-2 justify-center lg:justify-start mb-1">
                                                <span className="text-main-blue">{stat.icon}</span>
                                                <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-main-font">{stat.value}</span>
                                            </div>
                                            <div className="text-xs sm:text-sm text-main-subfont">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Phone Mockup */}
                            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                                <div className="relative w-full max-w-[280px] sm:max-w-[300px] lg:max-w-[320px]">
                                    {/* Glow effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-main-blue/20 to-main-th rounded-[3rem] sm:rounded-[4rem] blur-3xl scale-110 pointer-events-none" />

                                    {/* Phone frame */}
                                    <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2.5rem] sm:rounded-[3rem] p-2.5 sm:p-3 shadow-2xl">
                                        {/* Screen */}
                                        <div className="bg-main-white rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden">
                                            <div className="h-[480px] sm:h-[540px] lg:h-[580px] flex flex-col">
                                                {/* Status Bar */}
                                                <div className="bg-gradient-to-r from-main-th to-main-bg px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between">
                                                    <span className="text-[10px] sm:text-xs font-semibold text-main-font">9:41</span>
                                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                                        <div className="flex gap-0.5">
                                                            {[1, 2, 3, 4].map(i => (
                                                                <div
                                                                    key={i}
                                                                    className={`w-0.5 sm:w-1 rounded-full ${i <= 3 ? 'bg-main-font h-1.5 sm:h-2' : 'bg-main-border h-1 sm:h-1.5'}`}
                                                                />
                                                            ))}
                                                        </div>
                                                        <div className="w-4 sm:w-5 h-2 sm:h-2.5 border sm:border-2 border-main-font rounded-sm relative">
                                                            <div
                                                                className="absolute inset-0.5 bg-main-blue rounded-sm"
                                                                style={{ width: '70%' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* App Content */}
                                                <div className="flex-1 bg-gradient-to-b from-main-bg to-main-white p-4 sm:p-5">
                                                    {/* App Header */}
                                                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                                                        <div className="flex items-center gap-2.5 sm:gap-3">
                                                            <div className="relative">
                                                                <img src={Logo} alt="" className="h-9 w-9 sm:h-11 sm:w-11" />
                                                                <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-main-blue rounded-full border-2 border-main-white" />
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-main-font text-sm sm:text-lg">Caller Name</div>
                                                                <div className="text-[10px] sm:text-xs text-main-blue font-medium flex items-center gap-1">
                                                                    <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-main-blue rounded-full animate-pulse" />
                                                                    Active & Protected
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Incoming Call Card */}
                                                    <div className="bg-gradient-to-br from-main-white to-main-th rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg shadow-main-blue/10 mb-4 sm:mb-5 border border-main-blue/20 relative overflow-hidden">
                                                        <div className="absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 bg-main-blue/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                                                        <div className="text-[10px] sm:text-xs text-main-blue font-semibold mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
                                                            <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-main-blue opacity-75" />
                                                                <span className="relative inline-flex rounded-full h-full w-full bg-main-blue" />
                                                            </span>
                                                            Incoming Call
                                                        </div>
                                                        <div className="flex items-center gap-2.5 sm:gap-3">
                                                            <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-main-blue to-main-blue/70 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-main-blue/30 flex-shrink-0">
                                                                <span className="text-lg sm:text-2xl font-bold text-main-white">J</span>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-bold text-main-font text-sm sm:text-lg truncate">John Smith</div>
                                                                <div className="text-xs sm:text-sm text-main-subfont truncate">+1 (555) 123-4567</div>
                                                            </div>
                                                            <button
                                                                className="w-9 h-9 sm:w-11 sm:h-11 bg-main-blue rounded-full flex items-center justify-center shadow-lg shadow-main-blue/30 animate-pulse flex-shrink-0"
                                                                aria-label="Answer call"
                                                            >
                                                                <FaPhone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-main-white" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Recent Calls Header */}
                                                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                                                        <span className="text-[10px] sm:text-xs font-bold text-main-subfont uppercase tracking-wider">Recent Calls</span>
                                                        <span className="text-[10px] sm:text-xs text-main-blue font-medium cursor-pointer hover:underline">View All</span>
                                                    </div>

                                                    {/* Recent Calls List */}
                                                    <div className="space-y-0.5 sm:space-y-1">
                                                        {recentCalls.map((call, index) => (
                                                            <div
                                                                key={index}
                                                                className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl hover:bg-main-th/50 transition-colors cursor-pointer"
                                                            >
                                                                <div className={`w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-br ${call.color} rounded-lg sm:rounded-xl flex items-center justify-center shadow-sm flex-shrink-0`}>
                                                                    <span className="text-xs sm:text-sm font-bold text-white">{call.name[0]}</span>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="font-semibold text-main-font text-xs sm:text-sm truncate">{call.name}</div>
                                                                    <div className="text-[10px] sm:text-xs text-main-subfont truncate">{call.number}</div>
                                                                </div>
                                                                <div className="text-[10px] sm:text-xs text-main-subfont font-medium flex-shrink-0">{call.time}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Notch */}
                                        <div className="absolute top-4 sm:top-5 left-1/2 -translate-x-1/2 w-24 sm:w-28 h-6 sm:h-7 bg-black rounded-full flex items-center justify-center gap-2">
                                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-800 rounded-full" />
                                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-gray-700 rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section id="features" className="py-12 sm:py-14 lg:py-20 px-4 sm:px-6 lg:px-8 bg-main-white relative overflow-hidden">
                    {/* Background decorations */}
                    <div className="absolute inset-0 opacity-30 pointer-events-none">
                        <div className="absolute top-10 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-main-th rounded-full blur-3xl" />
                        <div className="absolute bottom-10 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-main-blue/5 rounded-full blur-3xl" />
                    </div>

                    <div className="max-w-7xl mx-auto relative">
                        {/* Section Header */}
                        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
                            <span className="inline-block text-main-blue font-bold text-xs sm:text-sm uppercase tracking-widest bg-main-th px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                                Features
                            </span>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-main-font mb-4 sm:mb-5">
                                Why Choose <span className="text-main-blue">Caller Name</span>?
                            </h2>
                            <p className="text-main-subfont text-base sm:text-lg max-w-2xl mx-auto">
                                A simple, effective caller ID solution that respects your privacy
                            </p>
                        </div>

                        {/* Features Grid */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className={`group bg-main-bg p-4 sm:p-5 lg:p-6 rounded-2xl sm:rounded-3xl border border-main-border hover:border-transparent transition-all duration-300 hover:shadow-2xl ${feature.bgGlow} hover:-translate-y-2 relative overflow-hidden`}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-main-white to-main-th opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                    <div className="relative">
                                        <div className={`w-11 h-11 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br ${feature.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center text-white mb-3 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                                            {feature.icon}
                                        </div>
                                        <h3 className="text-lg sm:text-xl font-bold text-main-font mb-1.5 sm:mb-2">{feature.title}</h3>
                                        <p className="text-main-subfont text-sm sm:text-base leading-relaxed">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How It Works Section */}
                <section id="how-it-works" className="py-12 sm:py-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-main-bg to-main-white">
                    <div className="max-w-5xl mx-auto">
                        {/* Section Header */}
                        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
                            <span className="inline-block text-main-blue font-bold text-xs sm:text-sm uppercase tracking-widest bg-main-th px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                                How It Works
                            </span>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-main-font mb-4 sm:mb-5">
                                Simple as <span className="text-main-blue">1-2-3</span>
                            </h2>
                        </div>

                        {/* Steps */}
                        <div className="grid sm:grid-cols-3 gap-6 sm:gap-5 lg:gap-6">
                            {steps.map((item, index) => (
                                <div key={index} className="text-center relative">
                                    {/* Connector line - hidden on mobile */}
                                    {index < 2 && (
                                        <div className="hidden sm:block absolute top-8 sm:top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-main-blue/50 to-transparent pointer-events-none" />
                                    )}
                                    <div className="relative inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 bg-gradient-to-br from-main-blue to-main-blue/80 rounded-2xl sm:rounded-3xl text-xl sm:text-2xl mb-3 sm:mb-4 shadow-xl shadow-main-blue/30">
                                        {item.icon}
                                    </div>
                                    <div className="text-[10px] sm:text-xs font-bold text-main-blue mb-1 sm:mb-1.5">STEP {item.step}</div>
                                    <h3 className="text-base sm:text-lg font-bold text-main-font mb-1 sm:mb-1.5">{item.title}</h3>
                                    <p className="text-main-subfont text-sm sm:text-base">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Download Section */}
                <section id="download" className="py-12 sm:py-14 lg:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                    {/* Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-main-blue via-main-blue/90 to-emerald-600" />
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full blur-3xl" />
                    </div>

                    <div className="max-w-4xl mx-auto text-center relative">
                        {/* Logo */}
                        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl sm:rounded-3xl shadow-2xl mb-5 sm:mb-6">
                            <img src={Logo} alt="" className="h-10 w-10 sm:h-12 sm:w-12" />
                        </div>

                        <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white mb-3 sm:mb-4">
                            Download Caller Name Today
                        </h2>
                        <p className="text-white/80 text-base sm:text-lg mb-6 sm:mb-8 max-w-xl mx-auto">
                            Take control of your incoming calls with our privacy-first caller identification app
                        </p>

                        {/* Store Buttons */}
                        <div className="flex flex-row gap-3 sm:gap-4 justify-center flex-wrap">
                            <a
                                href={playStoreLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-main-blue rounded-lg"
                                aria-label="Download from Google Play Store"
                            >
                                <img
                                    src={PlayStore}
                                    alt="Get it on Google Play"
                                    className="w-32 sm:w-36 lg:w-40"
                                    loading="lazy"
                                />
                            </a>
                            <a
                                href={appStoreLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-main-blue rounded-lg"
                                aria-label="Download from Apple App Store"
                            >
                                <img
                                    src={AppStore}
                                    alt="Download on the App Store"
                                    className="w-32 sm:w-36 lg:w-40"
                                    loading="lazy"
                                />
                            </a>
                        </div>
                    </div>
                </section>

                {/* Legal Section */}
                <section id="legal" className="py-12 sm:py-14 lg:py-20 px-4 sm:px-6 lg:px-8 bg-main-white">
                    <div className="max-w-4xl mx-auto">
                        {/* Section Header */}
                        <div className="text-center mb-6 sm:mb-8">
                            <span className="inline-block text-main-blue font-bold text-xs sm:text-sm uppercase tracking-widest bg-main-th px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                                Legal
                            </span>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-main-font">
                                Privacy Policy & Terms
                            </h2>
                        </div>

                        {/* Tab Buttons */}
                        <div className="flex justify-center mb-6 sm:mb-8">
                            <div className="inline-flex bg-main-bg rounded-xl sm:rounded-2xl p-1 sm:p-1.5 shadow-inner w-full sm:w-auto">
                                <button
                                    onClick={() => setActiveTab('privacy')}
                                    className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-3.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-inset ${activeTab === 'privacy'
                                        ? 'bg-main-blue text-main-white shadow-lg shadow-main-blue/30'
                                        : 'text-main-subfont hover:text-main-font'
                                        }`}
                                    aria-pressed={activeTab === 'privacy'}
                                >
                                    Privacy Policy
                                </button>
                                <button
                                    onClick={() => setActiveTab('terms')}
                                    className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-3.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-inset ${activeTab === 'terms'
                                        ? 'bg-main-blue text-main-white shadow-lg shadow-main-blue/30'
                                        : 'text-main-subfont hover:text-main-font'
                                        }`}
                                    aria-pressed={activeTab === 'terms'}
                                >
                                    Terms & Conditions
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="bg-main-bg rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-8 border border-main-border shadow-sm">
                            <div className="prose prose-sm sm:prose max-w-none text-main-subfont">
                                <p className="text-[10px] sm:text-xs text-main-subfont mb-4 sm:mb-6 bg-main-th inline-block px-2 sm:px-3 py-1 rounded-full">
                                    Last Updated: December 09, 2025
                                </p>
                                <p className="mb-4 sm:mb-6 text-sm sm:text-base">
                                    {activeTab === 'privacy'
                                        ? 'At Caller Name, we are committed to protecting your privacy. This policy explains how your information is handled.'
                                        : 'Welcome to Caller Name. By installing or using this App, you agree to be bound by these Terms and Conditions.'
                                    }
                                </p>

                                {(activeTab === 'privacy' ? privacyItems : termsItems).map(item => (
                                    <div key={item.num}>
                                        <h3 className="text-base sm:text-lg font-bold text-main-font mt-4 sm:mt-6 mb-1.5 sm:mb-2 flex items-center gap-2">
                                            <span className="w-6 h-6 sm:w-7 sm:h-7 bg-main-blue text-white rounded-md sm:rounded-lg flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                                                {item.num}
                                            </span>
                                            <span className="leading-tight">{item.title}</span>
                                        </h3>
                                        <p className="mb-2 sm:mb-3 text-sm sm:text-base leading-relaxed">{item.content}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section id="contact" className="py-12 sm:py-14 lg:py-20 px-4 sm:px-6 lg:px-8 bg-main-bg">
                    <div className="max-w-4xl mx-auto text-center">
                        <span className="inline-block text-main-blue font-bold text-xs sm:text-sm uppercase tracking-widest bg-main-th px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                            Support
                        </span>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-main-font mb-3 sm:mb-4">
                            Get in Touch
                        </h2>
                        <p className="text-main-subfont text-base sm:text-lg mb-6 sm:mb-8">
                            Have questions or feedback? We'd love to hear from you.
                        </p>
                        <a
                            href="mailto:contact@vedicantechnology.com"
                            className="inline-flex items-center gap-3 sm:gap-4 bg-main-blue text-main-white px-6 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:bg-main-blue/90 transition-all hover:shadow-2xl hover:shadow-main-blue/30 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2"
                        >
                            <HiMail className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                            <span className="break-all sm:break-normal">contact@vedicantechnology.com</span>
                        </a>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-main-font text-main-white py-8 sm:py-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-10">
                        {/* Brand */}
                        <div className="col-span-2 sm:col-span-2 lg:col-span-1">
                            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-main-white rounded-lg sm:rounded-xl flex items-center justify-center">
                                    <img src={Logo} alt="" className="h-5 w-5 sm:h-6 sm:w-6" />
                                </div>
                                <span className="text-base sm:text-lg font-bold">Caller Name</span>
                            </div>
                            <p className="text-main-white/60 text-xs sm:text-sm leading-relaxed">
                                Your privacy-first caller identification solution. Know who's calling without compromising your data.
                            </p>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Quick Links</h4>
                            <ul className="space-y-1.5 sm:space-y-2">
                                {['Features', 'Download', 'Privacy Policy', 'Terms'].map((item, i) => (
                                    <li key={i}>
                                        <button
                                            onClick={() => scrollToSection(i < 2 ? item.toLowerCase() : 'legal')}
                                            className="text-main-white/60 hover:text-main-white transition-colors text-xs sm:text-sm focus:outline-none focus:underline"
                                        >
                                            {item}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Download */}
                        <div>
                            <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Download</h4>
                            <ul className="space-y-1.5 sm:space-y-2">
                                <li>
                                    <a
                                        href={playStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-main-white/60 hover:text-main-white transition-colors text-xs sm:text-sm flex items-center gap-2"
                                    >
                                        <FaGooglePlay className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                        Google Play Store
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href={appStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-main-white/60 hover:text-main-white transition-colors text-xs sm:text-sm flex items-center gap-2"
                                    >
                                        <FaApple className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                        Apple App Store
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Contact */}
                        <div>
                            <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Contact</h4>
                            <a
                                href="mailto:contact@vedicantechnology.com"
                                className="text-main-white/60 hover:text-main-white transition-colors text-xs sm:text-sm flex items-center gap-2 break-all"
                            >
                                <HiMail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span>contact@vedicantechnology.com</span>
                            </a>
                        </div>
                    </div>

                    {/* Copyright */}
                    <div className="border-t border-main-white/10 pt-5 sm:pt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <p className="text-main-white/40 text-xs sm:text-sm text-center">
                            © {new Date().getFullYear()} Vedicant Technology. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>

            {/* Scroll to Top Button */}
            <button
                onClick={scrollToTop}
                className={`fixed bottom-4 sm:bottom-6 right-4 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 bg-main-blue text-white rounded-full shadow-lg shadow-main-blue/30 flex items-center justify-center transition-all duration-300 hover:bg-main-blue/90 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2 z-40 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
                    }`}
                aria-label="Scroll to top"
            >
                <FaChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
        </div>
    );
};

export default Client;